import React, { useEffect, useState } from "react";
import { useSnackbar } from "notistack";
import Divider from "@mui/material/Divider";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Grow from "@mui/material/Grow";
import Paper from "@mui/material/Paper";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Poppers from "@mui/material/Popper";
import MenuItem from "@mui/material/MenuItem";
import MenuList from "@mui/material/MenuList";
import Edit from "../../Edit";
import { useDeleteServiceMutation } from "services/services";
import { useGetSpecificRoleQuery } from "services/roles";
import { useDispatch, useSelector } from "react-redux";
import { setRole } from "rtkSlices/metaDataSlice";
import { solutionSettingsAll, hasSolutionTab } from "utils/partAccess";
import DeleteAlert from "components/Alerts/Delete";
import ShareSolution from "../../Share";
import { canOfferShare } from "utils/sharingSwitches";

export default function Menu(props) {
  const dispatch = useDispatch();
  const metaData = useSelector((state) => state.metaData);
  // Item 4: mirror the BE gates - editService gates on Solution Edit=ALL; Duplicate →
  // createService and, since D27 (a) (2026-09-25), deleteService gate on Solution
  // Creation=ALL (a DIFFERENT tab), so Delete follows the Duplicate check. ⊤ bypasses (baked
  // into the helpers). Edit stays openable as a read-only view (labelled "View").
  const canEdit = solutionSettingsAll(metaData);
  const canCreate = hasSolutionTab(metaData, "Solution Creation", "ALL");
  // Solution sharing (plan stage 7): "Share" for the solution's creator whose role holds Share, and for
  // grant-all admins (on any solution), while sharing is on; hidden for a creator who holds it only for
  // some groups (any of the role's rows for it).
  const grantAll = !!metaData.unscopedAdmin;
  const groupLimited = (metaData.services || []).some(
    (s) => String(s.id) === String(props.id) && s.group && s.group.id
  );
  const canShare = canOfferShare({
    sharingOn: metaData.solutionSharing === true,
    grantAll,
    ownModules: metaData.apps,
    isCreator: !!(props.service && props.service.userId) && String(props.service.userId) === String(window.localStorage.getItem("user")),
    groupLimited: !grantAll && groupLimited,
  });
  const [openShare, setOpenShare] = React.useState(false);
  let token = window.localStorage.getItem("token");
  const { enqueueSnackbar } = useSnackbar();
  const [openProfile, setOpenProfile] = React.useState(null);
  const [openEdit, setOpenEdit] = React.useState(false);
  function showSnackbar(title, message, variant, timeOut) {
    return enqueueSnackbar(
      { title, message: message ? message : "Something went wrong", variant },
      { timeOut }
    );
  }

  const [deleteService, deleteServiceResult] = useDeleteServiceMutation();
  const [activeId, setActiveId] = useState(null);
  const [deleteModal, setDelete] = useState(false);
  const roleRes = useGetSpecificRoleQuery(window.localStorage.getItem("role"), {
    skip: !deleteServiceResult.isSuccess,
  });
  async function toggleDelete(id = null) {
    setActiveId(id);
    setDelete((state) => !state);
  }

  useEffect(() => {
    if (roleRes.isSuccess) {
      dispatch(setRole(roleRes.data.payload));
    }
  }, [roleRes.isFetching]);

  useEffect(() => {
    if (deleteServiceResult.isSuccess) {
      showSnackbar(
        "Solution",
        deleteServiceResult.data?.message,
        "success",
        1000
      );
    }
    if (deleteServiceResult.isError) {
      showSnackbar(
        "Solution",
        deleteServiceResult.error?.data?.message,
        "error",
        1000
      );
    }
  }, [deleteServiceResult]);

  const handleClickProfile = (event, id) => {
    props.setBlur(id);
    if (openProfile && openProfile.contains(event.target)) {
      setOpenProfile(null);
    } else {
      setOpenProfile(event.currentTarget);
    }
  };

  const handleCloseProfile = () => {
    props.setBlur(false);
    setOpenProfile(null);
  };
  const handleClickOpen = () => {
    setOpenEdit(true);
  };
  const handleClose = () => {
    setOpenEdit(false);
  };
  return (
    <div
      style={{
        position: "absolute",
        right: "5px",
        top: "12px",
        cursor: "pointer",
      }}
    >
      <MoreVertIcon
        style={{ color: "#555555" }}
        onClick={(e) => handleClickProfile(e, props.id)}
      />
      <Poppers
        open={Boolean(openProfile)}
        anchorEl={openProfile}
        transition
        disablePortal
        placement="bottom-end"
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            id="profile-menu-list-grow"
            timeout={500}
            style={{
              transformOrigin: "center top",
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleCloseProfile}>
                <MenuList role="menu" style={{ minWidth: "100px" }}>
                  <MenuItem
                    onClick={ () => {
                      props.setDuplicationMode(false)
                      props.setSelected(props.service)
                    }}
                  >
                    {canEdit ? "Edit" : "View"}
                  </MenuItem>
                  {canCreate && <Divider />}
                  {canCreate && (
                    <MenuItem
                      onClick={ () => {
                        props.setDuplicationMode(true)
                        props.setSelected(props.service)
                      }}
                    >
                      Duplicate
                    </MenuItem>
                  )}
                  {canCreate && <Divider />}
                  {canCreate && (
                    <MenuItem onClick={() => toggleDelete(props.id)}>
                      Delete
                    </MenuItem>
                  )}
                  {canShare && <Divider />}
                  {canShare && (
                    <MenuItem
                      onClick={() => {
                        handleCloseProfile();
                        setOpenShare(true);
                      }}
                    >
                      Share Solution Access
                    </MenuItem>
                  )}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Poppers>
      {openEdit ? (
        <Edit
          key={props.id}
          service={props.service}
          updateService={props.updateService}
          open={true}
          handleClose={handleClose}
        />
      ) : null}
      {openShare ? (
        <ShareSolution
          open={openShare}
          onClose={() => setOpenShare(false)}
          serviceId={props.id}
          serviceName={props.service && props.service.name}
        />
      ) : null}
      {activeId ? (
        <DeleteAlert
          deleteModal={deleteModal}
          question="Are you sure you want to delete this solution?"
          id={activeId}
          handleDelete={(id) => {
            deleteService({ token, id });
            toggleDelete();
          }}
          handleClose={toggleDelete}
          deleteResult={deleteServiceResult}
        />
      ) : null}
    </div>
  );
}
